import { Component, DoCheck, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Models } from '@ibexfusion/models';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { AddItemsFromList } from '../../dialogs/add-items-from-list/add-items-from-list.component';
import { ProfileService } from '@services/profiles/profile.service';
import {
  GroupHasRoleInOrganisationService
} from '@services/groups/group-has-role-in-organisation.service';
import { PackageInstalledAppsService } from '@services/packages/package-installed-apps.service';
import { GroupHasSecuredAppService } from '@services/groups/group-has-secured-app.service';
import { GroupHasProfileService } from '@services/groups/group-has-profile.service';
import { PackageService } from '@services/packages/package.service';
import { CredentialsService } from '@services/credentials/credentials.service';
import { OrganisationService } from '@services/organisations/organisation.service';
import { GroupService } from '@services/groups/group.service';
import { DialogService } from '@services/dialog/dialog.service';
import { TranslateService } from '@services/translate/translate.service';
import { ExternalTokensService } from '@services/external-tokens/external-tokens.service';
import { AddNewToken } from '../../dialogs/add-new-token/add-new-token.component';

@Component({
    selector: 'profile-edit-form',
    templateUrl: './profile-edit-form.component.html',
    styleUrls: ['./profile-edit-form.component.scss'],
    providers: [
        ProfileService, GroupService, GroupHasRoleInOrganisationService, PackageInstalledAppsService, GroupHasSecuredAppService,
        PackageService, GroupHasProfileService, ExternalTokensService, CredentialsService, OrganisationService
    ],
    standalone: false
})
export class ProfileEditFormComponent implements OnInit, DoCheck {
  @Input() organisationId;
  @Input() itemId;

  @Output() changed: EventEmitter<boolean> = new EventEmitter();
  hasChanged = false;

  group: Models.Group;
  profile: Models.Profile;
  groupData: Models.GroupHasProfile[] = [];

  securedContent = false;
  orderModule = false;
  ticketingModule = false;

  missingEmail = false;
  existingEmail = false;
  missingFirstName = false;
  missingLastName = false;
  missingGroup = false;

  title: string;
  ghrio: Models.GroupHasRoleInOrganisation[];
  parentGroupData: Models.GroupHasProfile[] = [];
  childGroupData: Models.GroupHasProfile[] = [];
  ghp: Models.GroupHasProfile[];
  possibleGroupCount = 0;
  packages: Models.Package[];
  tokens: Models.ExternalToken[];
  packageInstalledApps: Models.PackageInstalledApp[];
  groupColumns = [{ prop: 'groupName' }];
  tokenColumns = [{ prop: 'title' }];
  tokenParams = {};
  headerButtonsGroup = [{ icon: 'add', callback: () => {return this.addGroup()} }];
  headerButtonsToken = [{ icon: 'add', callback: () => {return this.addToken()} }];
  addressbookRoles = ['chatModuleGroupAdmin', 'chatModule'];
  frontGroupRoles = ['securedContent', 'ticketingModule', 'ticketingModuleEditor', 'orderModule'];
  history: any;
  systemMessages: any;

  constructor(
    private route: ActivatedRoute, private profileService: ProfileService, private groupService: GroupService, private translate: TranslateService, private dialog: MatDialog,
    private ghrioService: GroupHasRoleInOrganisationService, private router: Router, private ghpService: GroupHasProfileService, private organisationService: OrganisationService,
    private dialogService: DialogService, public externalTokenService: ExternalTokensService
  ) {
  }

  @HostListener('window:beforeunload')
  @HostListener('window:beforeunload', ['$event'])
  canDeactivate(): Observable<boolean> | boolean | Promise<boolean> {
    return this.handleBeforeUnload();
  }

  async ngOnInit() {
    this.title = this.translate.get('userAdmin.profile.new');
    this.systemMessages = this.translate.get('system');
    this.tokenParams = this.itemId ? { ProfileID: this.itemId } : null;
    await this.initProfiles();
    this.initHistory();
  }

  async ngDoCheck() {
    if (this.hasChanged !== !this.handleBeforeUnload()) {
      this.hasChanged = !this.handleBeforeUnload();
      this.changed.emit(this.hasChanged);
    }
  }

  initHistory(): void {
    this.history = {};
    ['group', 'profile', 'securedContent', 'orderModule', 'ticketingModule', 'groupData', 'childGroupData', 'parentGroupData', 'tokens'].forEach(field => {
      this.history[field] = JSON.stringify(this[field])
    });
  }

  handleBeforeUnload() {
    return !Object.keys(this?.history || {}).some(key => this.history?.[key] !== JSON.stringify(this?.[key]));
  }

  async initProfiles() {
    this.profile = this.itemId ? await this.profileService.get(this.itemId) : new Models.Profile();
    if (this.itemId) {
      this.profile = await this.profileService.get(this.itemId);
      this.title = this.translate.get('userAdmin.profile.title') + ': ' + this.profile.email;
    } else {
      this.profile = new Models.Profile();
    }
    this.profile.userType = 'ibexfusion';
    this.profile.isAnonymous = false;
    this.ghrio = (await this.ghrioService.find(0, 1000, { OrganisationID: this.organisationId, GroupID: { $ne: 8 } })).data
    const org = await this.organisationService.get(this.organisationId);
    if (org.ParentOrganisationID) {
      const ghrioParent = (await this.ghrioService.find(0, 1000, { OrganisationID: org.ParentOrganisationID, GroupID: { $ne: 8 } })).data;
      this.parentGroupData = (await this.ghpService.find(0, 1000, { ProfileID: this.itemId, GroupID: { $in: ghrioParent.map(el => el.GroupID) } })).data;
    }
    const childOrgs = (await this.organisationService.find(0, 1000, { ParentOrganisationID: this.organisationId })).data;
    if (!!childOrgs?.length) {
      let childGhrio = (await this.ghrioService.find(0, 1000, { OrganisationID: { $in: childOrgs.map(el => el.OrganisationID) } })).data;
      childGhrio = childGhrio.filter(el => !this.frontGroupRoles.concat(this.addressbookRoles).includes(el.roleName) && el.GroupID.toString() !== '8');
      this.childGroupData = (await this.ghpService.find(0, 1000, { ProfileID: this.itemId, GroupID: { $in: childGhrio.map(el => el.GroupID) } })).data;
    }
    this.possibleGroupCount = [...new Set(this.ghrio.filter(el => !['48', '52', '53', '55', '49', '45'].includes(el.RoleID.toString())).map(el => el.GroupID))].length;
    const filtered = [...new Set(this.ghrio.filter(el => el.GroupID.toString() !== '8').map(el => el.GroupID))];
    this.groupData = (await this.ghpService.find(0, 1000, { ProfileID: this.itemId, GroupID: { $in: filtered } })).data;
    this.groupData = this.groupData.map(el => {
      if ((el?.groupName || '').startsWith('pref_')) {
        el.groupName = el.groupName.split('_').splice(2, el.groupName.split('_').length - 1).join('_');
      }
      return el;
    });
    this.ghp = JSON.parse(JSON.stringify(this.groupData));
    this.tokens = this.itemId ? (await this.externalTokenService?.find?.(0, 1000, { ProfileID: this.itemId }))?.data : [];
  }

  async save() {
    if (!this.itemId) {
      this.missingEmail = !this.checkEmail();
      if (!this.missingEmail) {
        this.existingEmail = (await this.profileService.find(0, 1, { email: this.profile.email })).data?.length !== 0;
      }
      this.missingFirstName = !this.profile.firstName || this.profile.firstName.length < 3;
      this.missingLastName = !this.profile.lastName || this.profile.lastName.length < 3;
      this.missingGroup = !this.groupData || !this.groupData.length;
      if (this.missingEmail || this.missingFirstName || this.missingLastName || this.missingGroup) {
        return;
      }
    }
    const res = !!this.itemId ? await this.profileService.update(this.itemId, this.profile, { query: { updateSettings: true } }) : await this.profileService.create(this.profile);
    const oldItems = this.ghp.filter(item => !this.arrayToMap(this.groupData, 'GroupID')[item.GroupID]);
    const newItems = this.groupData.filter(item => !this.arrayToMap(this.ghp, 'GroupID')[item.GroupID]);
    for (const oldItem of oldItems) {
      await this.ghpService.remove(oldItem.ID.toString());
    }
    for (const newItem of newItems) {
      await this.ghpService.create({ GroupID: newItem.GroupID, ProfileID: res.ProfileID?.length ? res.ProfileID : this.itemId });
    }
    const historyTokens = JSON.parse(this.history.tokens);
    const newTokens = this.tokens.filter(item => !item._id);
    const oldTokens = historyTokens.filter(el => !this.tokens.map(token => token._id).includes(el._id));
    for (let t of oldTokens) {
      await this.externalTokenService.remove(t._id);
    }
    for (let t of newTokens) {
      t.ProfileID = this.itemId;
      t.email = this.profile.email;
      await this.externalTokenService.create(t);
    }
    await this.router.navigate([this.router.url.split('/')[1], this.organisationId, 'profiles', res.ProfileID?.length ? res.ProfileID : this.itemId]);
    this.ngOnInit();
  }

  arrayToMap(array, idField = 'packageInstalledAppId') {
    if (!array) {
      return [];
    }
    return array.reduce((map, item) => {
      map[item[idField]] = item;
      return map;
    }, {});
  }

  async abort() {
    if (!!this.handleBeforeUnload()) {
      this.router.navigate([this.router.url.split('/')[1], this.route.snapshot.params['packageId'] || this.organisationId]);
    } else {
      const confirmation = await this.dialogService.confirm(this.systemMessages['general']['pendingChanges']);
      if (confirmation) {
        this.router.navigate([this.router.url.split('/')[1], this.route.snapshot.params['packageId'] || this.organisationId]);
      }
    }
  }

  async addGroup() {
    const groups = JSON.parse(JSON.stringify(this.groupData));
    let groupIds = this.ghrio.filter(el => !['48', '52', '53', '55', '49', '45'].includes(el.RoleID.toString())).map(el => el.GroupID);
    groupIds = [...new Set(groupIds)].filter(el => !this.groupData.map(sub => sub.GroupID.toString()).includes(el.toString()));
    let items = (await this.groupService.find(0, 1000, { GroupID: { $in: groupIds } })).data;
    items = items.map(el => {
      if ((el?.name || '').startsWith('pref_')) {
        el.name = el.name.split('_').splice(2, el.name.split('_').length - 1).join('_');
      }
      return el;
    });
    const dialogRef = this.dialog.open(AddItemsFromList, { width: '1200px', data: { availableItems: items, columns: [{ prop: 'name' }], idField: 'GroupID' } });
    const result = await dialogRef.afterClosed().toPromise();
    this.groupData = null;
    result?.additionalItems?.forEach(el => {
      groups.push({ GroupID: el, ProfileID: this.itemId, groupName: items.find(sub => sub.GroupID === el).name } as any);
    });
    setTimeout(() => {
      this.groupData = groups
    }, 0);
  }

  async removeGroup(event: any) {
    const items = JSON.parse(JSON.stringify(this.groupData.filter(el => el.GroupID !== event.row.GroupID)));
    this.groupData = null;
    setTimeout(() => { this.groupData = items }, 0);
  }

  async addToken() {
    const dialogRef = this.dialog.open(AddNewToken, { width: '1200px' });
    const result = await dialogRef.afterClosed().toPromise();
    if (!result) {
      return;
    }
    const tokens = JSON.parse(JSON.stringify(this.tokens));
    tokens.push(result);
    this.tokens = null;
    setTimeout(() => { this.tokens = tokens }, 0);
  }

  removeToken(event: any) {
    const tokens = JSON.parse(JSON.stringify(this.tokens.filter(el => JSON.stringify(el) !== JSON.stringify(event?.row))));
    this.tokens = null;
    setTimeout(() => { this.tokens = tokens }, 0);
  }

  checkEmail(): boolean {
    return (!this.profile.email || this.profile.email.trim() === '') ? false : !!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.profile.email);
  }
}
